import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * Weight-loss-quiz deriver. Wraps `deriveBoostProfile` (the quiz-v7 pattern),
 * same shape as the cortisol wrapper (quizzes/cortisol-profile.ts):
 *   1. Answer translation: the quiz asks the ad's fat-storage signs in the
 *      ad's own language. The 4 ids with a genuine quiz-v6 equivalent (`age`,
 *      `energy-3pm`, `body-composition`, `when-started`) reuse quiz-v6
 *      ids/values verbatim, so they pass through untranslated. Two honest
 *      translations round out the synthetic set:
 *        - `concern: "weight-loss"` is injected — the entire quiz's concern IS
 *          weight loss, so this reflects the premise, not a manufactured
 *          answer. It reinforces `visceral-adiposity` organically and lifts QoL
 *          to the body-composition domain. It adds NOTHING to tier1/tier2
 *          severity (countSignals reads `concern` only for sex-drive/muscle/
 *          focus), so severity stays driven by the real sign answers.
 *        - `transformation-goal` -> `results-want` feeds the (unrendered)
 *          expected-outcome so the profile stays internally coherent.
 *   2. Sign severity: the 5 fat-storage sign questions feed severity via the
 *      `extraSignals` seam (worst answer -> tier1, second-worst -> tier2,
 *      matching the quiz-v7 cohort-gradient convention).
 *   3. Forced primary: `visceral-adiposity` leads the verdict on every path via
 *      the `forcePrimaryCause` seam. Its locked explanation copy IS the ad's
 *      mechanism ("converting your testosterone into estrogen via the aromatase
 *      pathway, creating a self-reinforcing loop") = Bridge 2's payoff, so the
 *      quiz->verdict thread is congruent end-to-end. Unlike cortisol-dominance,
 *      visceral-adiposity is also scored organically (body-composition +
 *      concern=weight-loss), so on heavy-body paths it already leads; forcing
 *      it just guarantees it on the lean/young paths too. The organic top
 *      scorer stays visible as Secondary.
 *
 * Pure function of `answers` — reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim — copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "energy-3pm", "body-composition", "when-started"] as const;

/** Aspiration beat -> quiz-v6 results-want (expected-outcome only, unrendered). */
const GOAL_TO_RESULTS: Record<string, "muscle" | "sex-drive" | "all-of-above"> = {
  "flat-hard": "muscle",
  "drive-confidence": "sex-drive",
  "full-transformation": "all-of-above",
};

/**
 * Sign-question severity mapping. Worst answer -> tier1, second-worst -> tier2
 * (quiz-v7 cohort convention). tier1 carries the strongest low-T correlates:
 * chest softening (estrogen/aromatization), collapsed drive, and the diet-proof
 * belly. See docs/t-suppression-research.md for the tier rationale.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  "chest-change": ["yes-bothered"],
  "drive-drop": ["significantly"],
  "belly-diet": ["last-to-go"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  "fat-location": ["belly", "chest"],
  "weight-look": ["smaller-soft", "havent-lost"],
  "chest-change": ["somewhat"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "drive-drop": ["somewhat"],
  "belly-diet": ["shrinks-stalls", "everywhere-but-belly"],
  "weight-look": ["little-better"],
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

/**
 * Builds the ignite-namespace answer set the base deriver reads: the 4
 * passthrough ids, the injected weight-loss concern, and the translated
 * aspiration goal. No fabricated symptom answers — severity comes from the
 * `extraSignals` seam, not from manufactured quiz-v6 symptom values.
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  // The quiz's entire premise is weight loss (see spec §4).
  out["concern"] = "weight-loss";
  const goal = answers["transformation-goal"];
  if (typeof goal === "string" && goal in GOAL_TO_RESULTS) {
    out["results-want"] = GOAL_TO_RESULTS[goal]!;
  }
  return out;
}

export function deriveWeightlossProfile(answers: Answers): TSuppressionProfile {
  return deriveBoostProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "visceral-adiposity",
  });
}
